// NPM MODULES
import React, { useState, useEffect, useCallback, use } from 'react';
import {
  Box,
  Grid,
  Stack,
  Table,
  Link,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from '@mui/material';
import moment from 'moment';
import Loader from '../../common/Loader';
import Paginator from '../../common/Paginator';

// CONFIG
import config from '../../../Config';

import carrierReportsRequest from '../../../requests/CarrierReportsRequest';
// FORMATTED MESSAGES
import CarrierReportsFormattedMessages from '../../../messages/reports/CarrierReportsFormattedMessages';
import { getZoneOffsettedDate } from '../../../utils/dates';

const CarrierDetail = ({
  fromDate,
  toDate,
  carrier,
  errorHandler,
  navigate,
  updateActingProviderDetails,
  updateActingCustomerDetails,
}) => {
  const { getCarrierDetailReportsData } = carrierReportsRequest();
  const [data, setData] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    customerFilter: '',
    gatewayFilter: '',
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [rowCount, setRowCount] = useState(1);
  const [totals, setTotals] = useState({
    messages_sent: 0,
    direct_sent: 0,
    reminders_sent: 0,
    failed_messages: 0,
  });

  const handleCarrierDetailReportSearch = useCallback(async () => {
    try {
      setData([]);
      setIsSearching(true);
      console.log('searchParams', filters);

      const searchParams = {
        ...filters,
        fromDate: fromDate,
        toDate: toDate,
        gatewayCarrierIds: carrier.gateway_carrier_ids,
      };
      let results = await getCarrierDetailReportsData(
        searchParams,
        errorHandler
      );
      let total = {
        messages_sent: 0,
        direct_sent: 0,
        reminders_sent: 0,
        failed_messages: 0,
      };
      results.forEach((el) => {
        total.messages_sent = total.messages_sent + parseInt(el.messages_sent);
        total.direct_sent = total.direct_sent + parseInt(el.direct_sent);
        total.reminders_sent =
          total.reminders_sent + parseInt(el.reminders_sent);
        total.failed_messages =
          total.failed_messages + parseInt(el.failed_messages);
      });

      setData(results);
      setTotals(total);
      setRowCount(results.length);
      setIsSearching(false);
    } catch (error) {
      console.error(error);
      setData([]);
      setIsSearching(false);
    }
  }, [fromDate, toDate, carrier.gateway_carrier_ids, filters]);

  useEffect(() => {
    handleCarrierDetailReportSearch();
  }, [handleCarrierDetailReportSearch]);

  const changeEvent = async (i) => {
    setPageNumber(i);
  };

  /* Changes the amount of entries per pagination page. */
  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const handleList = () => {
    if (data && Array.isArray(data)) {
      const displayList = data.slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      );
      setDisplayList(displayList);
    } else {
      setRowCount(0);
      setDisplayList([]);
    }
  };

  useEffect(() => {
    handleList();
  }, [pageLength, pageNumber]);

  useEffect(() => {
    setPageNumber(1);
    handleList();
  }, [data]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const newFilters = { ...filters, [name]: value };
    console.log('newFilters', newFilters);
    setFilters(newFilters);
  };

  const handleCustomerNavigate = (item) => {
    const customerId = item.customer_id;
    const providerId = item.provider_id;
    window.history.pushState(
      customerId,
      `Customer:${customerId}`,
      `/admin/customers/${customerId}`
    );
    updateActingProviderDetails(providerId);
    updateActingCustomerDetails(customerId);
    navigate('/admin/customers/' + customerId);
  };

  // Adding the time range text fromDate to toDate
  const generateTimeRangeText = () => {
    return (
      <Typography gutterBottom>
        {CarrierReportsFormattedMessages.from} {getZoneOffsettedDate(fromDate)}{' '}
        {CarrierReportsFormattedMessages.to} {getZoneOffsettedDate(toDate)}
      </Typography>
    );
  };

  const generateTable = () => {
    return (
      <TableContainer
        sx={{
          marginBottom: 4,
          overflowX: 'auto',
          width: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        <Table id="inbox">
          <TableHead>
            <TableRow>
              <TableCell>
                {CarrierReportsFormattedMessages.customerName}
              </TableCell>
              <TableCell>
                {CarrierReportsFormattedMessages.customerCountry}
              </TableCell>
              <TableCell>{CarrierReportsFormattedMessages.gateway}</TableCell>
              <TableCell align="right">
                {CarrierReportsFormattedMessages.standardSent}
              </TableCell>
              <TableCell align="right">
                {CarrierReportsFormattedMessages.directSent}
              </TableCell>
              <TableCell align="right">
                {CarrierReportsFormattedMessages.reminderSent}
              </TableCell>
              <TableCell align="right">
                {CarrierReportsFormattedMessages.failedMessages}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayList.map((row, index) => (
              <TableRow key={index}>
                <TableCell name="customerName">
                  <Link
                    tabIndex={1}
                    onClick={() => handleCustomerNavigate(row)}
                  >
                    {row.customer_name}
                  </Link>
                </TableCell>
                <TableCell name="customerCountry">
                  {row.customer_country}
                </TableCell>
                <TableCell name="gateway">{row.gateway}</TableCell>
                <TableCell align="right" name="standardSent">
                  {row.messages_sent}
                </TableCell>
                <TableCell align="right" name="directSent">
                  {row.direct_sent}
                </TableCell>
                <TableCell align="right" name="reminderSent">
                  {row.reminders_sent}
                </TableCell>
                <TableCell align="right" name="failedMessage">
                  {row.failed_messages}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>{CarrierReportsFormattedMessages.total}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="right">{totals.messages_sent}</TableCell>
              <TableCell align="right">{totals.direct_sent}</TableCell>
              <TableCell align="right">{totals.reminders_sent}</TableCell>
              <TableCell align="right">{totals.failed_messages}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        {carrier.carrier_name}
      </Typography>
      {generateTimeRangeText()}
      <>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            rowGap: 2,
            marginBottom: 2,
          }}
        >
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack
              sx={{
                flexDirection: { lg: 'row' },
                gap: 2,
                alignItems: { lg: 'center' },
              }}
            >
              <TextField
                size="small"
                label="Customer Name"
                name="customerFilter"
                value={filters.customerFilter}
                onChange={(e) => handleInputChange(e)}
                placeholder="Search Customer Name"
                sx={{ flex: 1 }}
              />

              <TextField
                size="small"
                label="Gateway"
                name="gatewayFilter"
                value={filters.gatewayFilter}
                onChange={(e) => handleInputChange(e)}
                placeholder="Search Country"
                sx={{ flex: 1 }}
              />
            </Stack>
          </Grid>
        </Grid>
      </>
      {isSearching ? (
        <Grid size={12}>
          <Loader />
        </Grid>
      ) : data.length === 0 ? (
        <Typography>{CarrierReportsFormattedMessages.noResults}</Typography>
      ) : (
        <Grid size={12}>
          {generateTable()}
          <Paginator
            itemCount={rowCount}
            changeEvent={changeEvent}
            pageLength={pageLength}
            pageNumber={pageNumber}
            editFunction={changePageLength}
          />
        </Grid>
      )}
    </Box>
  );
};

export default CarrierDetail;
